import { api } from '../axios'
import type { ApiResponse, PaginationInfo } from '../types'
import type { MedicalRecord } from '../types'

export const medicalRecordsApi = {
  getAll: (page: number = 1, limit: number = 10, approvalStatus?: string) => {
    let q = `page=${page}&limit=${limit}`
    if (approvalStatus && approvalStatus !== 'all') {
      q += `&approvalStatus=${encodeURIComponent(approvalStatus)}`
    }
    return api.get<ApiResponse<MedicalRecord[]> & { pagination: PaginationInfo }>(`/medical-records?${q}`)
  },

  getTeachersWithoutRecords: () =>
    api.get<ApiResponse<{ id: number; teacher_name: string; school_id: string }[]>>('/medical-records/teachers-without-records'),

  create: (data: {
    teacherId: number | string
    ailmentName: string
    severity: 'Mild' | 'Moderate' | 'Severe' | 'Critical'
    diagnosisDate?: string
    treatmentStatus?: string
    remarks?: string
    documents?: string
  }) => api.post<ApiResponse<MedicalRecord>>('/medical-records', data),

  /** Teacher: own submissions (all statuses). */
  getSelf: () => api.get<ApiResponse<MedicalRecord[]>>('/medical-records/self'),

  createSelf: (data: {
    ailmentName: string
    severity: 'Mild' | 'Moderate' | 'Severe' | 'Critical'
    diagnosisDate?: string
    treatmentStatus?: string
    remarks?: string
    documents?: string
  }) => api.post<ApiResponse<MedicalRecord>>('/medical-records/self', data),

  getByTeacher: (teacherId: number | string, opts?: { approvedOnly?: boolean }) => {
    let path = `/medical-records/${teacherId}`
    if (opts?.approvedOnly) path += '?approvedOnly=1'
    return api.get<ApiResponse<MedicalRecord[]>>(path)
  },

  update: (
    id: number,
    data: Partial<{
      ailmentName: string
      severity: 'Mild' | 'Moderate' | 'Severe' | 'Critical'
      diagnosisDate?: string
      treatmentStatus?: string
      remarks?: string
      documents?: string
    }>
  ) => api.put<ApiResponse<MedicalRecord>>(`/medical-records/${id}`, data),

  /** Super Admin only — `deleteReason` is required by the API. */
  delete: (id: number, deleteReason: string) =>
    api.delete<ApiResponse>(`/medical-records/${id}`, { data: { deleteReason } }),

  /** Teacher: withdraw pending submission only (deletes DB row and upload file when applicable). */
  cancelSelf: (id: number) => api.delete<ApiResponse>(`/medical-records/self/${id}`),

  approve: (id: number, reviewRemarks?: string) =>
    api.patch<ApiResponse<MedicalRecord>>(`/medical-records/${id}/approve`, { reviewRemarks: reviewRemarks || undefined }),

  reject: (id: number, reviewRemarks: string) =>
    api.patch<ApiResponse<MedicalRecord>>(`/medical-records/${id}/reject`, { reviewRemarks }),
}
